import { Injectable } from "@angular/core";
import moment from "moment-timezone";
import * as timeSpace from "@mapbox/timespace";
import { IHistoryProject } from "../../interfaces/i-history-project";
import { IMetadataImage } from "../../interfaces/i-metadataimage";
import { GeoService } from "./geo.service";
import { Viewerlayer } from "../../model/viewerlayer";
import { IProjectAOI } from "../../interfaces/i-project-aoi";
import { ViewerConstants } from "../../../shared/model/viewer-constants";

@Injectable({
  providedIn: "root",
})
export class ImagesService {
  public static GSD_THRESHOLD = 10;

  public static HIGH = "high";

  public static ULTRA = "ultra";

  public static nadirOrientation = ["NN", "NE", "NS", "NW", "N*"];

  public static obliqueOrientation = ["N", "E", "S", "W"];

  public static DEFAULT_NADIR = "N*";

  /**
   * Convert array images in history projects array
   * Get each project code and create Object with formatted date and project code
   * @param {IMetadataImage[]} images
   * @returns {IHistoryProject[]}
   */
  static projectsFromImages(images: IMetadataImage[]): IHistoryProject[] {
    const projects: IHistoryProject[] = [];
    if (images?.length) {
      // get distinct images by project
      images.forEach((imageData) => {
        const project: IHistoryProject = {
          code: imageData.projectname,
          date: ImagesService.dateFromProjectCode(imageData.projectname) || null,
          layer: imageData.source_layer,
          children: [],
        };
        const oldProject = projects.find(
          (prj) => prj.code === imageData.projectname && prj.layer === imageData.source_layer,
        );
        let childAOI: IHistoryProject = null;
        if (imageData.child_aoi !== imageData.projectname) {
          childAOI = {
            code: imageData.child_aoi,
            date: ImagesService.dateFromProjectCode(imageData.projectname),
            layer: imageData.source_layer,
          };
          project.children.push(childAOI);
          if (oldProject && oldProject.children.findIndex((cAoi) => cAoi.code === childAOI.code) === -1) {
            oldProject.children.push(childAOI);
          }
        }

        if (!oldProject) {
          projects.push(project);
        }
      });
    }
    if (projects?.length) {
      projects.map((project) => {
        project.children.map((cAoi) => {
          cAoi.images = images.filter((img) => img.child_aoi === cAoi.code);
          cAoi.orientations = [
            ...new Set(
              cAoi.images
                // .filter((img) => !img.archived)
                .map((image) => ImagesService.normalizeOrientation(image.orientation)),
            ),
          ];
        });
        const projectImages = images
          .filter((img) => img.projectname === project.code && img.source_layer === project.layer)
          .sort((i1, i2) => (i1.capturedDate ? i1.capturedDate.getTime() - i2.capturedDate.getTime() : -1));
        project.minDate = projectImages[0].capturedDate;
        project.maxDate = projectImages[projectImages.length - 1].capturedDate;
        project.images = projectImages;
        project.orientations = [
          ...new Set(
            projectImages
              // .filter((img) => !img.archived)
              .map((image) => ImagesService.normalizeOrientation(image.orientation)),
          ),
        ];
        return project;
      });
    }
    return projects.sort((a, b) => a.maxDate.getTime() - b.maxDate.getTime());
  }

  /**
   * Create javascript date from project code
   * split project code in month and year and create
   * date object
   * @param {string} code
   * @returns {string}
   */
  static dateFromProjectCode(code: string): Date {
    if (code) {
      const year = 2000 + parseInt(code.substring(code.length - 4, code.length - 2), 10);
      const month = parseInt(code.substring(code.length - 2, code.length), 10) - 1; // zero based
      const day = Math.random() * (30 - 1) + 1; // get random day for projects who match the same month
      return new Date(year, month, day);
    }
    return null;
  }

  static sortImagesByCaptureDate(images: IMetadataImage[]): IMetadataImage[] {
    const sortedImages = images?.sort(
      (img1: IMetadataImage, img2: IMetadataImage) =>
        ImagesService.getCaptureDate(img2).getTime() - ImagesService.getCaptureDate(img1).getTime(),
    );
    return sortedImages;
  }

  static getNewestImage(images: IMetadataImage[], orientations: string[]): IMetadataImage {
    const filterImages = images?.filter((img) => orientations.includes(img.orientation));
    return ImagesService.sortImagesByCaptureDate(filterImages)[0];
  }

  /**
   * Return ultra/high depends on gsd value
   * @param image
   */
  static getImageResolution(image: IMetadataImage): string {
    const resolution = image.gsd * 100 > this.GSD_THRESHOLD ? this.HIGH : this.ULTRA;
    return resolution;
  }

  /**
   * Return label resolution for image
   * @param image
   */
  static getImageResolutionLabel(image: IMetadataImage): string {
    return this.getImageResolution(image) === ImagesService.HIGH ? "Wide Area" : "Urban";
  }

  static getCaptureDate(image: IMetadataImage): Date {
    return new Date(image.capture_date);
  }

  /**
   * Normalize orientation values, if is nadir, return N*
   * @param orientation
   */
  static normalizeOrientation(orientation: string) {
    const normalized = this.isNadir(orientation) ? this.DEFAULT_NADIR : orientation;
    return normalized;
  }

  /**
   * Check if the orientation is nadiral
   * @param orientation
   */
  static isNadir(orientation: string): boolean {
    const isNadir = this.nadirOrientation.includes(orientation);
    return isNadir;
  }

  static isOblique(orientation: string): boolean {
    return this.obliqueOrientation.includes(orientation);
  }

  /**
   * Get layer types from a list of images
   * Return blusky, graysky etc
   * @param images
   */
  static getLayerTypes(images: IMetadataImage[]): string[] {
    const layerTypes = [...new Set(images.map((image: IMetadataImage) => image.source_layer.split("-")[0]))];
    return layerTypes;
  }

  /**
   * Get collection types from a list of images
   * Return bluesky, graysky etc
   * @param images
   * @param collections
   */
  static getCollectionTypes(images: IMetadataImage[]): string[] {
    const collectionTypes = [
      ...new Set(
        images.map((image: IMetadataImage) => {
          if (ViewerConstants.COLLECTION_TYPE_LAYERS.bluesky.some((c) => image.source_layer.includes(c))) {
            return ViewerConstants.COLLECTION_PREFIX.BLUESKY;
          } else {
            return ViewerConstants.COLLECTION_PREFIX.GRAYSKY;
          }
        }),
      ),
    ];
    return collectionTypes;
  }

  /**
   * Extract resolutions for images [ultra, high]
   * @param images
   */
  static getResolutions(images: IMetadataImage[]): string[] {
    const res = [
      ...new Set(
        images.reduce((storage, image: IMetadataImage) => {
          storage.push(ImagesService.getImageResolution(image));
          return storage;
        }, []),
      ),
    ];
    return res;
  }

  /**
   * Get array of orientations for a image array
   * @param images
   */
  static getOrientations(images: IMetadataImage[]): string[] {
    const orientations = [...new Set(images.reduce((acc, img) => acc.concat(img.orientation), []))];
    return orientations;
  }

  /**
   * Filter images which scale percentage bbox no intersect with the view extent
   * @param images
   * @param extent
   * @param scale
   */
  static filterImagesInBBox(images: IMetadataImage[], extent, scale, enabledAOIs = []): IMetadataImage[] {
    const wktView = GeoService.bboxToWkt(extent);
    const geoJsonView = GeoService.wktToGeoJSONPolygon(wktView);
    const filtered = images.filter((image) => {
      const isEnabled = enabledAOIs
        ? enabledAOIs.find((aoi) => aoi.name === image.aoi || aoi.name === image.projectname)
        : true;
      if (isEnabled) {
        const imageBboxPolygon = GeoService.wktToGeoJSONPolygon(image.bbox_wkt_latlon, true);
        return GeoService.polygonsContains(imageBboxPolygon, geoJsonView);
      }
      return false;
    });
    return filtered;
  }

  /**
   * Return the newestImage for a layer list
   * @param layers
   * @param images
   */
  static newImageForLayer(layer: Viewerlayer, images: IMetadataImage[]): IMetadataImage {
    if (layer && images.length) {
      const sortedImages = images
        .filter((image) => layer.layers.includes(image.source_layer))
        .sort(
          (i1: IMetadataImage, i2: IMetadataImage) =>
            ImagesService.getCaptureDate(i2).getTime() - ImagesService.getCaptureDate(i1).getTime(),
        );
      if (sortedImages.length) {
        return sortedImages[0] ? sortedImages[0] : null;
      }
    }
    return null;
  }

  static visibleImageForLayers(visibleLayers: Viewerlayer[], images: IMetadataImage[]): IMetadataImage {
    if (visibleLayers.length && images.length) {
      const sortedImages = images
        .filter((image) => visibleLayers[0].layers.includes(image.source_layer))
        .sort(
          (i1: IMetadataImage, i2: IMetadataImage) =>
            ImagesService.getCaptureDate(i2).getTime() - ImagesService.getCaptureDate(i1).getTime(),
        );
      if (sortedImages.length) {
        return sortedImages[0] ? sortedImages[0] : null;
      }
    }
    return null;
  }

  static visibleImageForAOI(aoi: IProjectAOI, images: IMetadataImage[]): IMetadataImage {
    const img = aoi
      ? images
          .filter((image) => {
            if (aoi.getParentAOI()) {
              return image.child_aoi === aoi.getName();
            }
            return image.aoi === aoi.getName();
          })
          .sort(
            (i1: IMetadataImage, i2: IMetadataImage) =>
              ImagesService.getCaptureDate(i2).getTime() - ImagesService.getCaptureDate(i1).getTime(),
          )[0]
      : null;
    return img;
  }

  /**
   * Format image metadata capture date with locale
   * @param captureImage
   * @param format
   */
  static formatImageDate(captureImage: IMetadataImage, format = "MMMM Do, YYYY"): any {
    if (!captureImage) {
      return null;
    }

    let captureDate = ImagesService.transformLocalCaptureDateFromMetadata(captureImage);
    if (captureDate instanceof moment) {
      captureDate = moment(captureDate);
    }

    return captureDate && captureDate.isValid() ? captureDate.format(format) : null;
  }

  static transformLocalCaptureDateFromMetadata(image: IMetadataImage, zone = "UTC") {
    const centrePoints = GeoService.wktToPoint(image.centre_wkt_latlon);
    const date = moment.tz(image.capture_date, zone);
    return timeSpace.getFuzzyLocalTimeFromPoint(date, centrePoints.coordinates);
  }

  static filterByCollectionType(images: IMetadataImage[], collectionType: string): IMetadataImage[] {
    const values = ViewerConstants.COLLECTION_TYPE_LAYERS[collectionType];
    return values?.length ? images.filter((img) => values.some((type: string) => img.source_layer.includes(type))) : [];
  }
}
